import { useCallback, useMemo, useState } from 'react';
import { useReducedMotion } from 'motion/react';

import type { BriefSectionResponseDto } from '@inference.engine/workspace-api-client/web';
import { scrollBriefSectionIntoView } from '@/features/comment-on-file';

import { findMissingRequiredSections } from '../../lib/findMissingRequiredSections';

interface LiveSection {
  id: string;
  content: unknown;
  value?: unknown;
}

interface UseMissingRequiredSectionsParams {
  /** The form's live sections, in the order they render. */
  sections: readonly LiveSection[];
  /** The brief's own sections, which carry `required`, `retired` and `type`. */
  briefSections: readonly BriefSectionResponseDto[];
  onReady: () => void;
}

/**
 * Gates "Mark as ready" on the required sections the server would refuse. A refused attempt
 * marks every missing section and scrolls to the first; once attempted, a mark is derived from
 * the live answers, so it goes as soon as its section is filled.
 */
export function useMissingRequiredSections({
  sections,
  briefSections,
  onReady,
}: UseMissingRequiredSectionsParams) {
  const reducedMotion = useReducedMotion() ?? false;
  const [attempted, setAttempted] = useState(false);

  const missing = useMemo(() => {
    const byId = new Map(briefSections.map((s) => [s.id, s]));
    return findMissingRequiredSections(
      sections.map((s) => {
        const source = byId.get(s.id);
        return {
          id: s.id,
          required: source?.required ?? false,
          retired: source?.retired ?? false,
          type: source?.type ?? null,
          content: s.content,
          value: s.value ?? null,
        };
      }),
    );
  }, [sections, briefSections]);

  const missingSectionIds = useMemo(() => new Set(attempted ? missing : []), [attempted, missing]);

  const requestMarkReady = useCallback(() => {
    if (missing.length === 0) {
      onReady();
      return;
    }
    setAttempted(true);
    scrollBriefSectionIntoView(missing[0], { reducedMotion });
  }, [missing, onReady, reducedMotion]);

  return { missingSectionIds, requestMarkReady };
}
